/**
 * Feature Flags Hook
 *
 * Provides feature flag state management for multi-tenant control.
 * Features have two levels of control:
 * - Platform level (superadmin): Controls if feature is available to an org
 * - Organization level (admin): Controls if feature is enabled for org users
 *
 * A feature is effectively enabled when: platform_enabled AND org_enabled
 *
 * For A/B experiments, use useExperiment(flagName) which reads from PostHog.
 */

import { useState, useEffect, useCallback, useContext, createContext, useMemo } from 'react';
import posthog from 'posthog-js';
import { useAuth } from '../components/auth';
import { useOrganization } from './useOrganization';
import {
  getOrgFeatures,
  getFeatureStatus,
  toggleOrgFeature,
  FeatureKeys,
} from '../services/featuresApi';

// Feature flags cache (5 min TTL)
const CACHE_TTL = 5 * 60 * 1000;
const featureCache = new Map();

// Feature Flags Context
const FeatureFlagsContext = createContext(null);

/**
 * Get cached value if still valid
 */
const getCached = (key) => {
  const entry = featureCache.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
    return entry.value;
  }
  return null;
};

/**
 * Set cached value
 */
const setCached = (key, value) => {
  featureCache.set(key, {
    value,
    timestamp: Date.now(),
  });
};

/**
 * Invalidate cache entry
 */
const invalidateCache = (key = null) => {
  if (key) {
    featureCache.delete(key);
  } else {
    featureCache.clear();
  }
};

/**
 * Feature Flags Provider Component
 *
 * Wraps the application to provide feature flags context.
 * Automatically loads features for the current organization.
 */
export const FeatureFlagsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { currentOrg } = useOrganization();

  // State
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load all features for current organization
   */
  const loadFeatures = useCallback(async () => {
    if (!user || !isAuthenticated || !currentOrg) {
      setFeatures({});
      setLoading(false);
      return;
    }

    const cacheKey = `features:${currentOrg.id}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setFeatures(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orgFeatures = await getOrgFeatures(user, currentOrg.id);

      // Convert array to map for easy lookup
      const featuresMap = {};
      for (const feature of orgFeatures) {
        featuresMap[feature.feature_key] = {
          ...feature,
          // Ensure boolean values
          platformEnabled: Boolean(feature.platform_enabled),
          orgEnabled: Boolean(feature.org_enabled),
          effectiveEnabled: Boolean(feature.effective_enabled),
        };
      }

      setCached(cacheKey, featuresMap);
      setFeatures(featuresMap);
    } catch (err) {
      console.error('Failed to load features:', err);
      setError(err.message);
      // Default to empty features on error
      setFeatures({});
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated, currentOrg]);

  // Load features when org changes
  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  /**
   * Check if a feature is effectively enabled
   * (platform_enabled AND org_enabled)
   */
  const isEnabled = useCallback((featureKey) => {
    const feature = features[featureKey];
    if (!feature) return false;
    return feature.effectiveEnabled;
  }, [features]);

  /**
   * Check if a feature is platform-enabled
   */
  const isPlatformEnabled = useCallback((featureKey) => {
    const feature = features[featureKey];
    if (!feature) return false;
    return feature.platformEnabled;
  }, [features]);

  /**
   * Check if a feature is org-enabled
   */
  const isOrgEnabled = useCallback((featureKey) => {
    const feature = features[featureKey];
    if (!feature) return true; // Default to true if not configured
    return feature.orgEnabled;
  }, [features]);

  /**
   * Get full feature status
   */
  const getFeature = useCallback((featureKey) => {
    return features[featureKey] || null;
  }, [features]);

  /**
   * Toggle feature at org level (for admins)
   */
  const toggleFeature = useCallback(async (featureKey, enabled) => {
    if (!user || !currentOrg) {
      throw new Error('Authentication and organization required');
    }

    try {
      const updated = await toggleOrgFeature(user, currentOrg.id, featureKey, enabled);

      // Update local state
      setFeatures(prev => ({
        ...prev,
        [featureKey]: {
          ...prev[featureKey],
          ...updated,
          orgEnabled: Boolean(updated.org_enabled),
          effectiveEnabled: Boolean(updated.effective_enabled),
        },
      }));

      // Invalidate cache
      invalidateCache(`features:${currentOrg.id}`);
      invalidateCache(`feature:${currentOrg.id}:${featureKey}`);

      return updated;
    } catch (err) {
      console.error('Failed to toggle feature:', err);
      throw err;
    }
  }, [user, currentOrg]);

  /**
   * Refresh features from server
   */
  const refresh = useCallback(() => {
    if (currentOrg) {
      invalidateCache(`features:${currentOrg.id}`);
    }
    loadFeatures();
  }, [loadFeatures, currentOrg]);

  // Memoized feature access helpers
  const sandboxLabEnabled = useMemo(
    () => isEnabled(FeatureKeys.SANDBOX_LAB),
    [isEnabled]
  );

  const value = {
    // State
    features,
    loading,
    error,

    // Feature checks
    isEnabled,
    isPlatformEnabled,
    isOrgEnabled,
    getFeature,

    // Actions
    toggleFeature,
    refresh,

    // Quick access for common features
    sandboxLabEnabled,

    // Constants
    FeatureKeys,
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Hook to access feature flags context
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

/**
 * Hook to check a single feature flag
 * Returns { enabled, loading, platformEnabled, orgEnabled }
 */
export const useFeatureFlag = (featureKey) => {
  const { user, isAuthenticated } = useAuth();
  const { currentOrg } = useOrganization();

  const [status, setStatus] = useState({
    enabled: false,
    loading: true,
    platformEnabled: false,
    orgEnabled: true,
    error: null,
  });

  useEffect(() => {
    const fetchStatus = async () => {
      if (!user || !isAuthenticated || !currentOrg) {
        setStatus({
          enabled: false,
          loading: false,
          platformEnabled: false,
          orgEnabled: true,
          error: null,
        });
        return;
      }

      const cacheKey = `feature:${currentOrg.id}:${featureKey}`;
      const cached = getCached(cacheKey);
      if (cached) {
        setStatus({ ...cached, loading: false });
        return;
      }

      try {
        const result = await getFeatureStatus(user, currentOrg.id, featureKey);
        const newStatus = {
          enabled: Boolean(result.effective_enabled),
          loading: false,
          platformEnabled: Boolean(result.platform_enabled),
          orgEnabled: Boolean(result.org_enabled),
          error: null,
        };
        setCached(cacheKey, newStatus);
        setStatus(newStatus);
      } catch (err) {
        console.error(`Failed to fetch feature status for ${featureKey}:`, err);
        setStatus({
          enabled: false,
          loading: false,
          platformEnabled: false,
          orgEnabled: true,
          error: err.message,
        });
      }
    };

    fetchStatus();
  }, [user, isAuthenticated, currentOrg, featureKey]);

  return status;
};

/**
 * Higher-order component to gate features
 */
export const withFeatureGate = (WrappedComponent, requiredFeature, FallbackComponent = null) => {
  return function FeatureGatedComponent(props) {
    const { isEnabled, loading } = useFeatureFlags();

    if (loading) {
      return <div className="animate-pulse bg-white/5 rounded-xl p-4" />;
    }

    if (!isEnabled(requiredFeature)) {
      if (FallbackComponent) {
        return <FallbackComponent feature={requiredFeature} {...props} />;
      }
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Component to conditionally render based on feature flag
 */
export const FeatureGate = ({ feature, children, fallback = null }) => {
  const { isEnabled, loading } = useFeatureFlags();

  if (loading) {
    return <div className="animate-pulse bg-white/5 rounded-xl p-4 h-16" />;
  }

  if (!isEnabled(feature)) {
    return fallback;
  }

  return children;
};

/**
 * useExperiment — reads PostHog feature flags for A/B experiments.
 *
 * Separate from the backend-driven feature flag system. Use this for
 * UI experiments configured in the PostHog dashboard without a backend deploy.
 *
 * Returns:
 *   variant: string | boolean | null  (the flag value, or null if not loaded)
 *   isEnabled: boolean                (true when variant is truthy / 'test')
 *
 * Usage:
 *   const { variant, isEnabled } = useExperiment('new-onboarding-flow');
 *   if (isEnabled) { ... }
 */
export const useExperiment = (flagName) => {
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    if (!posthog.__loaded) return;

    // posthog.onFeatureFlags fires once flags are loaded
    const unsubscribe = posthog.onFeatureFlags(() => {
      const value = posthog.getFeatureFlag(flagName);
      setVariant(value ?? false);
    });

    // If flags are already loaded, read immediately
    const current = posthog.getFeatureFlag(flagName);
    if (current !== undefined) {
      setVariant(current ?? false);
    }

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [flagName]);

  return {
    variant,
    isEnabled: variant === true || variant === 'test' || (typeof variant === 'string' && variant !== 'control'),
  };
};

export default useFeatureFlags;
